import { reactive, getCurrentInstance, computed, onMounted } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { useReadinessEmployees } from "@/composables/readinessEmployees/useReadinessEmployees";
import { useEmployees } from "@/composables/employees/useEmployees";
import { transformChatterData } from "@/utils/chatterUtils";

// Create a singleton instance to ensure state consistency
let instance = null;

export function useReadinessEmployeeViewState() {
  if (instance) return instance;
  const filter = useFilters();
  const employees = useReadinessEmployees();
  const employee = useEmployees();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  // Load saved filters from localStorage
  const savedFilters = filter.getFilters("readinessEmployees");

  const state = reactive({
    filters: {
      values: savedFilters || [],
      searchText: "",
      showFilterDialog: false,
      key: "readinessEmployees",
      clearSelection: "",
      filterGroups: [
        {
          label: "Recalculation",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "Requires Recalc", value: "requires_recalc" },
          ],
        },
      ],
      needsRecalculation: false,
    },
    loading: {
      table: false,
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalEmployeeChatter: 0,
      page: 1,
    },
    table: {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    readiness: {
      average_readiness: 0,
      loading: false,
    },
    featureFlags: {
      employeeAvailabilitySelector: true, // Default value
    },
  });

  const resetPagination = () => {
    state.table.currentPage = 1;
  };

  // Initialize view state - reset pagination and search on component mount
  const initializeViewState = () => {
    resetPagination();
    clearSearchText();
  };

  const applyFilters = async (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);
    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;
    state.filters.showFilterDialog = false;

    // Reset pagination when filters are applied
    resetPagination();
    await loadEmployeesWithFilters();
  };

  const removeFilters = (removedFilters) => {
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedFilters;
  };
  const recalculateAllEmployees = async () => {
    proxy.$loader.show();

    // Update the employee store's pagination state to match our viewState BEFORE making API call
    if (employees.employeeStore) {
      employees.employeeStore.setPagination({
        currentPage: state.table.currentPage,
        itemsPerPage: state.table.itemsPerPage,
        totalCount: employees.employeeStore.getTotalEmployeesCount,
      });
    }

    // Build query parameters to preserve current state
    const queryParams = {};

    // Add pagination parameters
    queryParams.limit = state.table.itemsPerPage;
    queryParams.offset =
      (state.table.currentPage - 1) * state.table.itemsPerPage;

    // Add search parameter for first_name
    if (state.filters.searchText && state.filters.searchText.trim()) {
      queryParams.first_name = state.filters.searchText.trim();
    }

    // Add filter parameters
    if (state.filters.values.includes("requires_recalc")) {
      queryParams.needs_recalc = true;
    }

    // Add ordering
    queryParams.ordering = "first_name";

    // Add status filter to only get active employees
    queryParams.status = "active";

    // Add archived filter to exclude archived employees
    queryParams.archived = false;

    await employees.recalculateEmployees(queryParams);

    proxy.$loader.hide();
  };

  const currentEmployee = computed(
    () =>
      state.assignmentWizard.currentEmployee ||
      state.relieveAssignment.currentEmployee
  );

  const getEmployeeCardProps = (employee) => {
    // Check feature flag for availability selector
    const isAvailabilitySelectorEnabled =
      state.featureFlags.employeeAvailabilitySelector;

    const hideAvailabilitySelector =
      !isAvailabilitySelectorEnabled || employee?.primary_position || false;

    return {
      firstName: employee.first_name,
      lastName: employee.last_name,
      position: employee?.primary_position
        ? employee.primary_position.name
        : "",
      // position: employee.title,
      photoUrl: employee.avatar,
      availability: employee.availability,
      showAvailability: true,
      nameUrl: `/organization/employees/${employee.id}/pathtoreadiness`,
      showLinkUnderline: false,
      hideAvailabilitySelector: hideAvailabilitySelector,
      isAllocated: employee?.primary_position || false,
    };
  };
  const getEmployeeReadinessProps = (employee) => {
    if (employee.needs_recalc) {
      state.filters.needsRecalculation = true;
    }
    return {
      recalc: employee.needs_recalc,
      readiness: employee.current_readiness,
      status: "Has Assignments",
      name: employee.primary_position?.name,
      code: employee.primary_position?.code_str?.split(" ")[0] || null,
    };
  };
  const getEmployeePrimaryReadinessProps = (employee) => {
    return {
      recalc: false,
      readiness: employee.primary_readiness,
      status: getEmployeePrimaryReadinessStatus(employee),
      name: employee.primary_position?.name,
      code: employee.primary_position?.code_str?.split(" ")[0] || null,
      showImportance: true,
      importance: employee.primary_importance,
    };
  };
  const getEmployeePrimaryReadinessStatus = (employee) => {
    if (!employee.primary_position) {
      return "No Assignments";
    } else {
      return "Has Assignments";
    }
  };
  const getEmployeePrepareReadinessProps = (employee) => {
    const firstPreparingPosition = employee.preparing_for_positions?.[0];
    return {
      recalc: false,
      readiness: employee.preparing_for_readiness,
      status: getEmployeePrepareReadinessStatus(employee),
      name: firstPreparingPosition?.name,
      code: firstPreparingPosition?.code_str?.split(" ")[0] || null,
      showImportance: true,
      importance: employee.preparing_for_importance,
    };
  };
  const getEmployeePrepareReadinessStatus = (employee) => {
    if (
      !employee.preparing_for_positions ||
      employee.preparing_for_positions.length === 0
    ) {
      return "No Assignments";
    } else {
      return "Has Assignments";
    }
  };
  const getSwitcherProps = (employee) => {
    return {
      label: "",
      value: employee.exclude_from_avg,
      left: "-25px",
      toggleLoader: state.readiness.loading,
    };
  };

  // Métodos para filtros
  const updateFilter = (filterName, value) => {
    if (filterName in state.filters) {
      state.filters[filterName] = value;
    }
  };

  const clearFilters = () => {
    state.filters = {
      status: "all",
      department: null,
      position: null,
    };
  };

  const clearSearchText = () => {
    state.filters.searchText = "";
  };

  // Métodos para loading
  const setLoading = (key, value) => {
    if (key in state.loading) {
      state.loading[key] = value;
    }
  };
  const getEmployeeChatter = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      state.openChatterModal.show = true;
      const response = await employee.employeeChatter(
        accountID,
        "employee",
        state.openChatterModal.page
      );
      if (response) {
        const chatterItems = transformChatterData(response.data.results);
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalEmployeeChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };
  const openEmployeeChatterModal = async () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.show = true;
    await getEmployeeChatter();
  };
  const closeEmployeeChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalEmployeeChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };
  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getEmployeeChatter();
  };
  const loadAccountDetails = async () => {
    state.readiness.loading = true;
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const response = await employee.getAccountDetails(accountID);
    if (response) {
      setTimeout(() => {
        state.readiness.average_readiness =
          response.data.avg_employee_readiness;
        state.readiness.loading = false;
      }, 1500);
    }
  };

  const handleTablePageChange = async (page) => {
    state.table.currentPage = page;
    await employees.loadEmployeesPage(page, state.table.itemsPerPage);
  };

  const handleTableItemsPerPageChange = async (itemsPerPage) => {
    state.table.itemsPerPage = itemsPerPage;
    state.table.currentPage = 1; // Reset to first page
    await employees.loadEmployeesPage(1, itemsPerPage);
  };

  // Debounce function for search
  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  // Perform search with proper filtering
  const performSearch = async (searchText) => {
    // Always clear existing search filters first
    if (state.filters.searchText) {
      // Clear previous search
      state.filters.searchText = "";
    }

    // Only add search filters and load employees if there's actually a search term
    if (searchText && searchText.trim() !== "") {
      state.filters.searchText = searchText.trim();
    }

    // Reset to first page when searching
    resetPagination();

    // Load employees with current filters (including search)
    await loadEmployeesWithFilters();
  };

  // Debounced search function
  const debouncedSearch = debounce(performSearch, 500);

  // Handle search input
  const handleSearch = (searchText) => {
    debouncedSearch(searchText);
  };

  // Load employees with current filters
  const loadEmployeesWithFilters = async () => {
    // Update the employee store's pagination state to match our viewState BEFORE making API call
    const employeeStore = employees.employeeStore;
    if (employeeStore) {
      employeeStore.setPagination({
        currentPage: state.table.currentPage,
        itemsPerPage: state.table.itemsPerPage,
        totalCount: employeeStore.getTotalEmployeesCount,
      });
    }

    const queryParams = {};

    // Add pagination parameters
    queryParams.limit = state.table.itemsPerPage;
    queryParams.offset =
      (state.table.currentPage - 1) * state.table.itemsPerPage;

    // Add search parameter for first_name
    if (state.filters.searchText && state.filters.searchText.trim()) {
      queryParams.name = state.filters.searchText.trim();
    }

    // Add filter parameters
    if (state.filters.values.includes("requires_recalc")) {
      queryParams.needs_recalc = true;
    }

    // Add ordering
    queryParams.ordering = "first_name";

    // Add status filter to only get active employees
    queryParams.status = "active";

    // Add archived filter to exclude archived employees
    queryParams.archived = false;

    await employees.loadAllEmployees(queryParams);
  };
  const updateEmployeeExcludeFromAvgValue = async (item, val) => {
    // Get the actual current page from the store since view state might be reset
    const actualCurrentPage = employees.employeeStore.getPagination.currentPage;
    const actualItemsPerPage =
      employees.employeeStore.getPagination.itemsPerPage ||
      state.table.itemsPerPage;

    // Update view state to match store
    state.table.currentPage = actualCurrentPage;
    state.table.itemsPerPage = actualItemsPerPage;

    state.readiness.loading = true;
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const payload = {
      emp_id: item.id,
      value: val.value,
    };
    await employee.updateEmployeeExcludeFromAvg(accountID, payload);

    // Update the employee store's pagination state to match our viewState before loading
    if (employees.employeeStore) {
      employees.employeeStore.setPagination({
        currentPage: state.table.currentPage,
        itemsPerPage: state.table.itemsPerPage,
        totalCount: employees.employeeStore.getTotalEmployeesCount,
      });
    }

    // Use loadEmployeesWithFilters to preserve query parameters
    await loadEmployeesWithFilters();
    await loadAccountDetails();
  };

  // Initialize feature flags
  const initializeFeatureFlags = async () => {
    const employeeAvailabilitySelector = await proxy.$getFlag(
      "employee_availability_selector",
      true
    );
    state.featureFlags.employeeAvailabilitySelector =
      employeeAvailabilitySelector;
  };

  // Load feature flags on mount
  onMounted(async () => {
    await initializeFeatureFlags();
  });

  const composableInstance = {
    state,
    currentEmployee,
    updateFilter,
    clearFilters,
    setLoading,
    getEmployeeCardProps,
    getEmployeeReadinessProps,
    getEmployeePrimaryReadinessProps,
    getEmployeePrepareReadinessProps,
    applyFilters,
    removeFilters,
    recalculateAllEmployees,
    openEmployeeChatterModal,
    closeEmployeeChatterModal,
    requestMoreChatter,
    getSwitcherProps,
    loadAccountDetails,
    updateEmployeeExcludeFromAvgValue,
    handleTablePageChange,
    handleTableItemsPerPageChange,
    handleSearch,
    loadEmployeesWithFilters,
    initializeFeatureFlags,
    initializeViewState,
    clearSearchText,
  };

  instance = composableInstance;
  return composableInstance;
}
